// src/pages/Dashboard.jsx
import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../shared/supabase";

export default function Dashboard() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, averageRating: 0 });
  const [selectedReview, setSelectedReview] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [copyPopup, setCopyPopup] = useState(""); // for custom copy popup

  const reviewsPerPage = 30;
  const originalTitle = "Review Dashboard";
  const subscribedRef = useRef(false);

  // Format date
  const formatDate = (dateString) =>
    new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  // Fetch stats
  const fetchStats = async () => {
    try {
      const { count, error } = await supabase.from("feedbacks").select("*", { count: "exact" });
      if (error) throw error;

      const { data: avgData, error: avgError } = await supabase.from("feedbacks").select("rating");
      if (avgError) throw avgError;

      const ratings = avgData.map(r => r.rating);
      const averageRating = ratings.length
        ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(2)
        : 0;

      setStats({ total: count, averageRating });
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  // Fetch reviews
  // Fetch reviews - make sure contacted column is included
  const fetchReviews = async (page = 1) => {
    setLoading(true);
    try {
      const startIndex = (page - 1) * reviewsPerPage;
      const { data, error } = await supabase
        .from("feedbacks")
        .select("id, name, email, phone, message, rating, created_at, contacted") // ✅ Explicitly include contacted
        .order("created_at", { ascending: false })
        .range(startIndex, startIndex + reviewsPerPage - 1);
      if (error) throw error;

      setReviews(data || []);
      setCurrentPage(page);

      const { count } = await supabase.from("feedbacks").select("*", { count: "exact" });
      setTotalPages(Math.ceil(count / reviewsPerPage));
    } catch (err) {
      console.error("Error fetching reviews:", err);
    } finally {
      setLoading(false);
    }
  };

  // Realtime subscription
  const setupRealtimeSubscription = () => {
    if (subscribedRef.current) return;

    const channel = supabase
      .channel("feedbacks-changes")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "feedbacks" },
        (payload) => {
          setReviews(prev => [payload.new, ...prev]);

          setStats(prev => {
            const newTotal = prev.total + 1;
            const newAverage = (
              (parseFloat(prev.averageRating) * prev.total + payload.new.rating) / newTotal
            ).toFixed(2);
            return { total: newTotal, averageRating: newAverage };
          });

          if (!document.hasFocus()) {
            document.title = `1 New Feedback - ${originalTitle}`;
          }

          setTotalPages(prev => Math.ceil((prev * reviewsPerPage + 1) / reviewsPerPage));
        }
      )
      .subscribe(status => {
        if (status === "SUBSCRIBED") subscribedRef.current = true;
      });

    return channel;
  };

  // Clear tab notification on focus
  useEffect(() => {
    const handleFocus = () => {
      document.title = originalTitle;
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  // Initial fetch + subscription
  useEffect(() => {
    const initializeData = async () => {
      await fetchStats();
      await fetchReviews(1);
    };
    initializeData();
    const channel = setupRealtimeSubscription();

    return () => {
      if (channel) supabase.removeChannel(channel);
      subscribedRef.current = false;
    };
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) fetchReviews(newPage);
  };

  // Mark review as contacted
  // Mark review as contacted - CORRECTED VERSION
  const markContacted = async (id) => {
    try {
      const { error } = await supabase
        .from("feedbacks")
        .update({ contacted: true })
        .eq("id", id)
        .select(); // select returns the updated row

      if (error) throw error;

      // Update local state with updated row
      setReviews(prev =>
        prev.map(r => r.id === id ? { ...r, contacted: true } : r)
      );
    } catch (err) {
      console.error("Error marking review as contacted:", err.message);
    }
  };


  // Copy text to clipboard with custom popup
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopyPopup(`Copied: ${text}`);
      setTimeout(() => setCopyPopup(""), 1000); // hide after 1 second
    });
  };

  const filteredReviews = reviews.filter(review =>
    review.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    review.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    review.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`text-sm ${i < rating ? "text-yellow-500" : "text-gray-300"}`}>★</span>
    ));

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) startPage = Math.max(1, endPage - maxVisiblePages + 1);

    pages.push(
      <button key="prev" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">Previous</button>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button key={i} onClick={() => handlePageChange(i)} className={`px-3 py-2 rounded-lg border ${currentPage === i ? "bg-blue-500 text-white border-blue-500" : "border-gray-300 hover:bg-gray-50"}`}>{i}</button>
      );
    }

    pages.push(
      <button key="next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">Next</button>
    );

    return pages;
  };

  return (
  <div className="min-h-screen bg-gray-50 p-6 relative">
    {/* Custom copy popup */}
    {copyPopup && (
      <div className="fixed top-5 right-5 bg-black text-white px-4 py-2 rounded shadow-lg z-50">
        {copyPopup}
      </div>
    )}

    <div className="w-[95vw] mx-auto"> {/* Change this line */}
      
      {/* Header & Stats */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Review Dashboard</h1>
        <div className="grid grid-cols-2 lg:grid-cols-2 gap-4 mt-4 lg:mt-0">
          <div className="bg-white rounded-lg border p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
            <div className="text-sm text-gray-600 mt-1">Total Reviews</div>
          </div>
          <div className="bg-white rounded-lg border p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.averageRating}</div>
            <div className="text-sm text-gray-600 mt-1">Avg Rating</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border p-6 mb-6">
        <input type="text" placeholder="Search reviews..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" />
      </div>

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-lg border p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading reviews...</p>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="bg-white rounded-lg border overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full table-auto"> {/* Changed to table-auto */}
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Customer</th> {/* Added width */}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">Contact</th> {/* Added width */}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[200px]">Message</th> {/* Reduced width */}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Rating</th> {/* Added width */}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Date</th> {/* Added width */}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Contacted</th> {/* Added width */}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReviews.map(review => (
                  <tr key={review.id} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => setSelectedReview(review)}>
                    <td className="px-4 py-4 text-gray-900 truncate max-w-[160px]">{review.name}</td> {/* Truncate long names */}
                    <td className="px-4 py-4 text-gray-900">
                      <div 
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(review.email); }} 
                        className="cursor-pointer truncate max-w-[180px]"
                        title={review.email} // Show full email on hover
                      >
                        {review.email}
                      </div>
                      <div 
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(review.phone); }} 
                        className="cursor-pointer text-gray-500 truncate max-w-[180px]"
                        title={review.phone} // Show full phone on hover
                      >
                        {review.phone}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-gray-900 max-w-[200px] truncate" title={review.message}> {/* Reduced max width */}
                      {review.message}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <div className="flex mr-2">{renderStars(review.rating)}</div>
                        <span className="text-sm text-gray-600">({review.rating}/5)</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(review.created_at)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {review.contacted ? (
                        <span className="text-green-600 font-semibold text-sm">Contacted</span>
                      ) : (
                        <button 
                          onClick={(e) => { e.stopPropagation(); markContacted(review.id); }} 
                          className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition text-sm"
                        >
                          Mark Contacted
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredReviews.length === 0 && (
            <div className="text-center py-16">
              <div className="text-6xl text-gray-300 mb-4">📭</div>
              <h3 className="text-xl font-semibold text-gray-500 mb-2">No reviews found</h3>
              <p className="text-gray-400">Try adjusting your search</p>
            </div>
          )}
        </div>
      )}

        {/* Pagination */}
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">Showing {filteredReviews.length} of {reviewsPerPage} reviews on page {currentPage}</div>
          <div className="flex gap-2">{renderPagination()}</div>
        </div>

        {/* Modal */}
        {selectedReview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Review Details</h2>
                  <button onClick={() => setSelectedReview(null)} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Name</label>
                    <p className="text-gray-900 text-lg">{selectedReview.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Date</label>
                    <p className="text-gray-900 text-lg">{formatDate(selectedReview.created_at)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                    <p className="text-gray-900 text-lg">{selectedReview.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Phone</label>
                    <p className="text-gray-900 text-lg">{selectedReview.phone}</p>
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-600 mb-2">Rating</label>
                  <div className="flex items-center space-x-4">
                    <div className="flex space-x-1">{renderStars(selectedReview.rating)}</div>
                    <span className="text-gray-900 font-semibold text-lg">{selectedReview.rating} out of 5</span>
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-600 mb-2">Message</label>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-gray-700 leading-relaxed">{selectedReview.message}</p>
                  </div>
                </div>
                <div className="flex justify-end pt-6 border-t border-gray-200">
                  <button onClick={() => setSelectedReview(null)} className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium">Close</button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
